import type { IConfPersonasPaciente } from '@/modules/Administracion/interfaces'
import type { ICitaTechnologiasSalud } from '@/modules/Asistencial/interfaces'

/**
 * Campos escalares del paciente congelados en tbl_cita_technologiassalud
 * por el trigger setear_datos_persona al crear la tecnología.
 */
const camposSnapshot = [
  'copnNombre1',
  'copnNombre2',
  'copnApellido1',
  'copnApellido2',
  'copeIdentificacion',
  'copeTipoIdentificacion',
  'copeDireccion',
  'copeTelefono',
  'copeTelalternativo',
  'copeEmail',
  'copeTipoDivipola',
  'coppFechanacimiento',
  'coppTipoBarrio',
  'coppTipoSexo',
  'coppTipoZona',
  'coppTipoPoblacion',
  'coppTipoListPoblaciones',
  'coppTipoNiveleducativo',
  'coppTipoOcupacion',
  'coppTipoEstadocivil',
  'coppTipoIncapacidad',
  'coppTipoDivipola',
  'coppTipoOrientacion',
  'coppTipoGenero'
] as const

/** Maestras del paciente → asociación que las resuelve sobre la tecnología. */
const lookupsSnapshot = {
  tipoidentificacion: 'snapTipoId',
  sexo: 'snapSexo',
  divipola: 'snapDivipola',
  divipolanacimiento: 'snapDivipolaNac',
  poblacion: 'snapPoblacion',
  orientacion: 'snapOrientacion',
  zona: 'snapZona',
  ocupacion: 'snapOcupacion',
  civil: 'snapCivil',
  educativo: 'snapEducativo',
  incapacidad: 'snapIncapacidad'
} as const

/** Indica si la tecnología trae datos del paciente congelados. */
export function tieneSnapshotPaciente(technologia?: ICitaTechnologiasSalud | null): boolean {
  return !!(technologia?.copeIdentificacion || technologia?.copnNombre1)
}

/**
 * Devuelve los datos del paciente tal como estaban al momento de la atención.
 *
 * Parte del paciente vivo y sobreescribe campo por campo con el snapshot de la
 * tecnología. Las atenciones anteriores al trigger no tienen snapshot y caen a
 * los datos vigentes. Un campo del snapshot en null tampoco pisa al dato vivo:
 * ante un snapshot incompleto se prefiere mostrar el valor actual a dejar el
 * campo vacío. Las maestras (sexo, ocupación, ciudad…) solo se reemplazan si la
 * asociación snap* viene cargada, lo que exige pedir withPacienteSnapshot al
 * consultar la admisión.
 *
 * La EPS y el régimen no se congelan: salen del contrato, que es fijo por admisión.
 */
export function mergePacienteSnapshot(
  paciente?: Partial<IConfPersonasPaciente> | null,
  technologia?: ICitaTechnologiasSalud | null
): IConfPersonasPaciente {
  const datos = { ...(paciente || {}) } as IConfPersonasPaciente
  if (!tieneSnapshotPaciente(technologia)) return datos

  const tech = technologia as ICitaTechnologiasSalud & Record<string, unknown>
  const destino = datos as unknown as Record<string, unknown>

  for (const campo of camposSnapshot) {
    const valor = tech[campo]
    if (valor !== undefined && valor !== null) destino[campo] = valor
  }

  for (const [campo, asociacion] of Object.entries(lookupsSnapshot)) {
    const valor = tech[asociacion]
    if (valor) destino[campo] = valor
  }

  // copeShortName y copeFullName son virtuales del paciente vivo: hay que rearmarlos.
  datos.copeShortName = [datos.copnNombre1, datos.copnApellido1].filter(Boolean).join(' ')
  datos.copeFullName = [
    datos.copnNombre1,
    datos.copnNombre2,
    datos.copnApellido1,
    datos.copnApellido2
  ].filter(Boolean).join(' ')

  return datos
}
